/**
 * My Measurements State
 *
 * Manages measurements for the current user's students with real-time Firestore
 * sync and local caching. Automatically watches AccountState.studentIds() and
 * reloads whenever it changes. The state (including its localStorage cache) is
 * always fully reset when studentIds changes, guaranteeing no stale data across
 * caseload transitions.
 *
 * Collection path: partners/{teamId}/measurements
 * Filter:          studentId IN accountState.studentIds()
 *
 * @example
 * const measurements = myObservationsState.data();
 * const count        = myObservationsState.count();
 *
 * // Get all measurements for one student
 * const forStudent = myObservationsState.getObservationsByStudentId('student-123');
 *
 * // Force a full refresh from Firestore
 * await myObservationsState.forceRefresh();
 */

import { Injectable, inject, computed, signal, effect } from '@angular/core';
import { Unsubscribe } from '@angular/fire/firestore';
import { FirestoreService, QueryOptions } from 'src/app/core/services/firestore/firestore';
import { StateCacheService } from 'src/app/core/services/state-cache.service';
import type { Observation } from 'src/app/domain/observations/types/observations.types';
import { AccountState } from 'src/app/store/account';
import {
  daysAgoMs,
  observationTimestamp,
  RECENT_WINDOW_DAYS,
} from 'src/app/domain/analytics';
import {
  MyObservationsInitConfig,
  MY_OBSERVATIONS_DEFAULTS,
} from './my-observations.types';

@Injectable({
  providedIn: 'root',
})
export class MyObservationsState {
  // =============================================
  // DEPENDENCIES
  // =============================================
  private readonly firestoreService = inject(FirestoreService);
  private readonly stateCacheService = inject(StateCacheService);
  private readonly accountState = inject(AccountState);

  // =============================================
  // PRIVATE STATE (Writable Signals)
  // =============================================
  private readonly _data = signal<Observation[]>(MY_OBSERVATIONS_DEFAULTS.data);
  private readonly _initialized = signal<boolean>(MY_OBSERVATIONS_DEFAULTS.initialized);
  private readonly _loading = signal<boolean>(MY_OBSERVATIONS_DEFAULTS.loading);
  private readonly _error = signal<string | null>(MY_OBSERVATIONS_DEFAULTS.error);
  private readonly _lastUpdated = signal<number | null>(MY_OBSERVATIONS_DEFAULTS.lastUpdated);
  private readonly _lastSync = signal<number | null>(MY_OBSERVATIONS_DEFAULTS.lastSync);
  private readonly _hydratedFromCache = signal<boolean>(MY_OBSERVATIONS_DEFAULTS.hydratedFromCache);
  private readonly _activeTeamId = signal<string | null>(MY_OBSERVATIONS_DEFAULTS.activeTeamId);
  private readonly _studentIds = signal<string[]>(MY_OBSERVATIONS_DEFAULTS.studentIds);

  // =============================================
  // PUBLIC STATE (Readonly Signals)
  // =============================================

  readonly data = this._data.asReadonly();
  readonly initialized = this._initialized.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly lastUpdated = this._lastUpdated.asReadonly();
  readonly lastSync = this._lastSync.asReadonly();
  readonly hydratedFromCache = this._hydratedFromCache.asReadonly();
  readonly activeTeamId = this._activeTeamId.asReadonly();
  readonly studentIds = this._studentIds.asReadonly();

  // =============================================
  // COMPUTED STATE (Derived Values)
  // =============================================

  readonly isReady = computed(
    () => this.initialized() && !this.loading() && !this.error()
  );

  readonly hasData = computed(() => this.data().length > 0);

  readonly count = computed(() => this.data().length);

  /**
   * Observations inside the trailing `RECENT_WINDOW_DAYS` window
   * (canonical 30 days). Notebook analytics widgets that filter
   * observations by "recent" should bind to this signal rather than
   * rebuilding `data().filter(...)` inside their own `computed()`.
   */
  readonly recentObservations = computed(() => {
    const cutoff = daysAgoMs(RECENT_WINDOW_DAYS);
    return this._data().filter(obs => {
      const ts = observationTimestamp(obs);
      return ts !== null && ts >= cutoff;
    });
  });


  /** Get all measurements for a specific student */
  getObservationsByStudentId(studentId: string): Observation[] {
    return this.data().filter(m => m.studentId === studentId);
  }

  /** Get all measurements for a specific skill */
  getObservationsBySkillId(skillId: string): Observation[] {
    return this.data().filter(m => m.skillId === skillId);
  }

  /** Get a single measurement by its document ID */
  getObservationById(id: string): Observation | null {
    return this.data().find(m => m.id === id) ?? null;
  }

  // =============================================
  // PRIVATE SUBSCRIPTIONS
  // =============================================
  private _realtimeUnsubscribes: Unsubscribe[] = [];
  private _isInitializing = false;
  private _effectDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private _activeProfileId: string | null = null;

  // Listener merge queue (prevents race conditions across multiple batch listeners)
  private _listenerMergeQueue: Array<{ batchIndex: number; measurements: Observation[] }> = [];
  private _isProcessingMerge = false;

  // =============================================
  // CONSTRUCTOR - AUTO-REACTIVE INITIALIZATION
  // =============================================

  constructor() {
    effect(() => {
      const studentIds = this.accountState.studentIds();
      const activeTeam = this.accountState.activeTeam();
      const activeTeamId = activeTeam?.id || null;
      const activeProfileId = this.accountState.activeProfileId();
      const profilesLoaded = this.accountState.hasProfiles();

      const prevStudentIds = this._studentIds();
      const prevTeamId = this._activeTeamId();
      const prevProfileId = this._activeProfileId || null;

      const studentIdsChanged =
        JSON.stringify([...prevStudentIds].sort()) !==
        JSON.stringify([...studentIds].sort());
      const teamChanged = prevTeamId !== activeTeamId;
      const profileChanged = prevProfileId !== activeProfileId;

      // Case 1: Profiles not loaded yet — wait
      if (!profilesLoaded) {
        return;
      }

      // Case 2: No team — clear state
      if (!activeTeamId) {
        if (this._initialized()) this.reset();
        return;
      }

      // Case 3: No studentIds — clear state
      if (!studentIds || studentIds.length === 0) {
        if (this._initialized()) this.reset();
        return;
      }

      // Case 4: Team changed — reset and reinitialize immediately
      if (teamChanged && this._initialized()) {
        this.reset();
        this._activeProfileId = activeProfileId;
        this.initialize({ activeTeamId, studentIds }).catch(console.error);
        return;
      }

      // Case 5: Profile changed (same team) — reset and reinitialize immediately
      if (profileChanged && this._initialized() && activeTeamId) {
        this.reset();
        this._activeProfileId = activeProfileId;
        this.initialize({ activeTeamId, studentIds }).catch(console.error);
        return;
      }

      if (!this._activeProfileId) {
        this._activeProfileId = activeProfileId;
      }

      if (this._effectDebounceTimer) {
        clearTimeout(this._effectDebounceTimer);
      }

      // Cases 6 & 7: Debounce studentIds changes and initial load
      this._effectDebounceTimer = setTimeout(() => {
        // Case 6: studentIds changed (same team, same profile) — reset + reload
        if (studentIdsChanged && this._initialized() && !this._isInitializing) {
          this.reset();
          this.initialize({ activeTeamId, studentIds }).catch(console.error);
          return;
        }

        // Case 7: Initial load
        if (!this._initialized() && !this._isInitializing && activeTeamId && studentIds.length > 0) {
          this.initialize({ activeTeamId, studentIds }).catch(console.error);
        }
      }, 200);
    });
  }

  // =============================================
  // LIFECYCLE & INITIALIZATION
  // =============================================

  async initialize(config: MyObservationsInitConfig): Promise<void> {
    if (this._isInitializing) {
      console.warn('[MyObservationsState] Initialization already in progress');
      return;
    }

    this._isInitializing = true;
    this._loading.set(true);
    this._error.set(null);
    this._activeTeamId.set(config.activeTeamId);
    this._studentIds.set(config.studentIds);


    try {
      // STEP 1: Hydrate from localStorage cache
      const cached = this.stateCacheService.loadStateCache<Observation[]>(
        'my-measurements',
        config.activeTeamId
      );

      if (cached) {
        this._data.set(cached.data);
        this._lastSync.set(cached.lastSync);
        this._hydratedFromCache.set(true);
        this._initialized.set(true);
        this._loading.set(false);

      }

      // STEP 2: Load measurements from active team + additional teams in parallel
      const [activeMeasurements, ...additionalMeasurementBatches] = await Promise.all([
        this.loadMeasurements(config.activeTeamId, config.studentIds),
        ...(config.additionalTeamContexts ?? []).map(ctx =>
          this.loadMeasurements(ctx.teamId, ctx.studentIds)
        ),
      ]);

      const allMeasurements = [activeMeasurements, ...additionalMeasurementBatches].flat();

      // STEP 3: Set data
      this._data.set(allMeasurements);

      // STEP 4: Update metadata
      this._initialized.set(true);
      const now = Date.now();
      this._lastUpdated.set(now);
      this._lastSync.set(now);

      // STEP 5: Persist to localStorage (cache covers active team only)
      this.saveCache(config.activeTeamId, now);

      // STEP 6: Start real-time listener for active team only
      if (config.enableRealtimeUpdates !== false) {
        this.startRealtimeListener(config.activeTeamId, config.studentIds);
      }

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
      this._error.set(errorMessage);
      console.error('[MyObservationsState] ❌ Initialization failed:', error);
      throw error;
    } finally {
      this._loading.set(false);
      this._isInitializing = false;
    }
  }

  reset(): void {
    this.stopRealtimeListener();

    if (this._effectDebounceTimer) {
      clearTimeout(this._effectDebounceTimer);
      this._effectDebounceTimer = null;
    }

    this._listenerMergeQueue = [];
    this._isProcessingMerge = false;

    // Always clear cache on reset — guarantees fresh data after studentIds or team change
    const teamId = this._activeTeamId();
    if (teamId) {
      this.stateCacheService.clearStateCache('my-measurements', teamId);
    }

    this._data.set(MY_OBSERVATIONS_DEFAULTS.data);
    this._initialized.set(MY_OBSERVATIONS_DEFAULTS.initialized);
    this._loading.set(MY_OBSERVATIONS_DEFAULTS.loading);
    this._error.set(MY_OBSERVATIONS_DEFAULTS.error);
    this._lastUpdated.set(MY_OBSERVATIONS_DEFAULTS.lastUpdated);
    this._lastSync.set(MY_OBSERVATIONS_DEFAULTS.lastSync);
    this._hydratedFromCache.set(MY_OBSERVATIONS_DEFAULTS.hydratedFromCache);
    this._activeTeamId.set(MY_OBSERVATIONS_DEFAULTS.activeTeamId);
    this._studentIds.set(MY_OBSERVATIONS_DEFAULTS.studentIds);
    this._isInitializing = false;
    this._activeProfileId = null;

  }

  // =============================================
  // PUBLIC METHODS
  // =============================================

  /**
   * Optimistically remove all measurements belonging to a student from local state.
   * Call this immediately after a student is deleted so the UI clears before
   * the Firestore real-time listener catches up.
   */
  removeByStudentId(studentId: string): void {
    this._data.update(measurements => measurements.filter(m => m.studentId !== studentId));
  }

  /** Force a full refresh from Firestore, clearing the cache first */
  async forceRefresh(): Promise<void> {
    const teamId = this._activeTeamId();
    const studentIds = this._studentIds();

    if (!teamId) {
      console.warn('[MyObservationsState] Cannot force refresh - no active team');
      return;
    }

    if (!studentIds || studentIds.length === 0) {
      console.warn('[MyObservationsState] Cannot force refresh - no studentIds');
      return;
    }

    this.stateCacheService.clearStateCache('my-measurements', teamId);
    this._lastSync.set(null);
    this._hydratedFromCache.set(false);

    await this.initialize({ activeTeamId: teamId, studentIds, enableRealtimeUpdates: true });
  }

  /** Alias for forceRefresh */
  async refresh(): Promise<void> {
    return this.forceRefresh();
  }

  /** Clear error state */
  clearError(): void {
    this._error.set(null);
  }

  /**
   * Optimistically remove a set of measurements from local state.
   * Called immediately after a successful Firestore batch delete so the UI
   * updates without waiting for the next real-time snapshot.
   *
   * @param measurementIds - The Firestore document IDs to remove
   */
  removeMeasurements(measurementIds: string[]): void {
    if (measurementIds.length === 0) return;
    const idSet = new Set(measurementIds);
    this._data.update(current => current.filter(m => !m.id || !idSet.has(m.id)));
    this._lastUpdated.set(Date.now());
  }

  // =============================================
  // PRIVATE METHODS (Helpers)
  // =============================================

  private chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  /**
   * Save current data to localStorage.
   * Quota exceeded errors are caught and logged — the app continues without caching.
   */
  private saveCache(activeTeamId: string, now: number): void {
    try {
      this.stateCacheService.saveStateCache(
        'my-measurements',
        activeTeamId,
        this._data(),
        now
      );
    } catch (error) {
      console.warn('[MyObservationsState] Cache save failed (quota exceeded), continuing without cache');
    }
  }

  // =============================================
  // PRIVATE METHODS (Data Loading)
  // =============================================

  /**
   * Load measurements from Firestore filtered to the given studentIds.
   * Supports incremental updates via lastSync (adds updatedAt > lastSync filter).
   * Batches studentIds in groups of 10 to respect Firestore's `in` query limit.
   */
  private async loadMeasurements(
    activeTeamId: string,
    studentIds: string[],
  ): Promise<Observation[]> {
    if (studentIds.length === 0) {
      return [];
    }

    const path = `partners/${activeTeamId}/observations`;
    const batches = this.chunkArray(studentIds, 10);


    try {
      const allMeasurements: Observation[] = [];

      for (const batch of batches) {
        const batchResults = await this.firestoreService.getCollectionAsync<Observation>(
          path,
          { where: [{ field: 'studentId', operator: 'in', value: batch }] }
        );

        allMeasurements.push(...batchResults);
      }


      return allMeasurements;
    } catch (error) {
      console.error('[MyObservationsState] Failed to load measurements:', error);
      throw error;
    }
  }

  /**
   * Merge new/updated measurements into existing data.
   * Uses measurement.id as the deduplication key.
   * Filters out any measurements whose studentId is no longer in the active set.
   */
  private mergeMeasurements(newMeasurements: Observation[]): void {
    const currentData = this._data();
    const studentIds = this._studentIds();

    const dataMap = new Map(currentData.map(m => [m.id, m]));

    newMeasurements.forEach(m => {
      if (m.id) {
        dataMap.set(m.id, m);
      }
    });

    const filtered = Array.from(dataMap.values()).filter(
      m => m.studentId && studentIds.includes(m.studentId)
    );

    this._data.set(filtered);
    this._lastUpdated.set(Date.now());

  }

  // =============================================
  // PRIVATE METHODS (Real-time Listener)
  // =============================================

  /**
   * Queue a listener batch update for sequential processing.
   * Prevents race conditions when multiple batch listeners fire simultaneously.
   */
  private async queueListenerUpdate(
    batchIndex: number,
    measurements: Observation[]
  ): Promise<void> {
    this._listenerMergeQueue.push({ batchIndex, measurements });

    if (!this._isProcessingMerge) {
      await this.processListenerMergeQueue();
    }
  }

  /** Process queued listener updates sequentially */
  private async processListenerMergeQueue(): Promise<void> {
    if (this._isProcessingMerge) return;

    this._isProcessingMerge = true;

    try {
      while (this._listenerMergeQueue.length > 0) {
        const update = this._listenerMergeQueue.shift();
        if (!update) continue;

        const { measurements } = update;
        const studentIds = this._studentIds();
        const dataMap = new Map(this._data().map(m => [m.id, m]));

        measurements.forEach(m => {
          if (m.id && studentIds.includes(m.studentId)) {
            dataMap.set(m.id, m);
          }
        });

        this._data.set(Array.from(dataMap.values()));
        this._lastUpdated.set(Date.now());

        await new Promise(resolve => setTimeout(resolve, 0));
      }
    } finally {
      this._isProcessingMerge = false;
    }
  }

  /**
   * Start real-time listeners for the measurements collection.
   * One listener per batch of 10 studentIds (Firestore `in` limit).
   * Uses studentId IN [batch] filter only — no lastSync filter on listeners.
   */
  private startRealtimeListener(activeTeamId: string, studentIds: string[]): void {
    if (studentIds.length === 0) {
      console.warn('[MyObservationsState] Cannot start listener - empty studentIds');
      return;
    }

    this.stopRealtimeListener();

    const path = `partners/${activeTeamId}/observations`;
    const batches = this.chunkArray(studentIds, 10);


    batches.forEach((batch, batchIndex) => {
      const unsubscribe = this.firestoreService.onCollectionChange<Observation>(
        path,
        (batchMeasurements: Observation[]) => {

          if (batches.length === 1) {
            // Single batch: direct set (no race condition possible)
            const filtered = batchMeasurements.filter(
              m => m.studentId && studentIds.includes(m.studentId)
            );
            this._data.set(filtered);
            this._lastUpdated.set(Date.now());
          } else {
            // Multiple batches: queue to prevent race conditions
            this.queueListenerUpdate(batchIndex, batchMeasurements);
          }
        },
        {
          where: [{ field: 'studentId', operator: 'in', value: batch }],
        }
      );

      this._realtimeUnsubscribes.push(unsubscribe);
    });

  }

  /** Stop all real-time listeners and clear the merge queue */
  private stopRealtimeListener(): void {
    if (this._realtimeUnsubscribes.length > 0) {
      this._realtimeUnsubscribes.forEach(u => u());
      this._realtimeUnsubscribes = [];
      this._listenerMergeQueue = [];
      this._isProcessingMerge = false;

    }
  }
}
